<template>
  <Wrapper class="q-py-xl q-px-lg">
    <BackButton />
    <h5 class="q-mb-sm">Conversation Details</h5>
    <div class="row">
      <p class="q-mb-lg col-12 col-sm-6 col-md-6 q-pr-md">
        Viewing conversation <strong>{{ id }}</strong>
      </p>
      <p class="q-mb-lg col-12 col-sm-6 col-md-3">
        RFQ ID: <strong>{{ rfqID | formatId }}</strong>
      </p>
      <p class="q-mb-lg col-12 col-sm-6 col-md-3">
        Booking ID: <strong>{{ bookingID | formatId }}</strong>
      </p>
    </div>

    <div class="bg-gray q-mb-md q-pa-md">
      <div class="row items-center q-col-gutter-md">
        <div class="col-12 col-sm-6 col-md-4">
          <q-input v-model.trim="search" hint="Search message content" label="Search" />
        </div>

        <div class="col-12 col-sm-6 col-md-2">
          <q-select v-model="sort" emit-value map-options label="Sort" hint="" :options="options" />
        </div>

        <div class="col-12 col-sm-6 col-md-2">
          <q-btn class="full-width" color="primary" icon="search" label="search" @click="debouncedGetRequest" />
        </div>

        <div class="col-12 col-sm-6 col-md-2">
          <q-btn flat class="full-width" color="primary" icon="cached" label="reset" @click="reset" />
        </div>
      </div>
    </div>

    <DataTable ref="table" wrap-cells :columns="columns">
      <template v-if="isLoading">
        <tr v-for="n in 10" :key="n">
          <td v-for="m in columns.length" :key="m">
            <q-skeleton type="text" />
          </td>
        </tr>
      </template>

      <tr v-for="message in messages" v-else :key="message.id">
        <td>
          <template v-if="message.fromShipper">
            <div class="q-mb-xs">
              <span class="text-uppercase">{{ shipper.name }}</span> -
              <span class="text-capital">{{ shipper.businessName }}</span>
            </div>
            <div class="text-muted">{{ shipper.email }}</div>
          </template>
          <template v-else>
            <div class="q-mb-xs">
              <span v-if="transporter.company.repDisplayName">
                {{ transporter.company.repDisplayName }}
              </span>
              <span v-else class="text-capitalize">
                {{ transporter.company.repFirstName }}
              </span>
              -
              <span class="text-uppercase">
                {{ transporter.company.tradingName }}
              </span>
            </div>
            <div class="text-muted">{{ transporter.email }}</div>
          </template>
        </td>

        <td>{{ message.content }}</td>
        <td><q-icon v-if="message.flagged" color="black" name="flag" size="sm" /></td>
        <td>{{ message.createdAt | formatDateTime }}</td>

        <td class="q-gutter-x-sm">
          <span class="action" @click="openDialog('edit', message)">Edit</span>
          <span class="action" @click="openDialog('delete', message)">Delete</span>
        </td>
      </tr>
    </DataTable>

    <Pagination v-if="totalPages > 1" :page.sync="currentPage" :total="totalPages" />

    <!-- Edit dialog -->
    <q-dialog v-model="isEditDialogOpen" :persistent="isLoading">
      <q-card style="width: 700px; max-width: 80vw">
        <q-card-section>
          <h6>Edit Message</h6>
        </q-card-section>

        <q-card-section v-if="selectedMsg">
          <q-input v-model.trim="selectedMsg.content" autofocus autogrow filled label="Content" />
        </q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup flat color="muted" label="Cancel" @click="selectedMsg = null" />

          <q-btn
            color="primary"
            label="Confirm"
            :disable="selectedMsg && !selectedMsg.content"
            :loading="isLoading"
            @click="editMessage"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Delete dialog -->
    <q-dialog v-model="isDeleteDialogOpen" :persistent="isLoading">
      <q-card>
        <q-card-section>
          <h6>Delete Message Confirm</h6>
        </q-card-section>

        <q-card-section v-if="selectedMsg">
          <p>
            Are you sure you want to delete this message from
            <strong v-if="selectedMsg.fromShipper">{{ shipper.email }}</strong>
            <strong v-else>{{ transporter.email }}</strong
            >?
          </p>

          <div class="bg-gray q-pa-md">
            <p class="q-ma-none">{{ selectedMsg.content }}</p>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup flat color="muted" label="Cancel" @click="selectedMsg = null" />
          <q-btn color="negative" label="Delete" :loading="isLoading" @click="deleteMessage" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </Wrapper>
</template>

<script>
import _debounce from 'lodash/debounce';
import get from 'lodash/get';
import API from '@/apis';
import Wrapper from '@/components/Wrapper.vue';
import DataTable from '@/components/DataTableNew.vue';
import Pagination from '@/components/Pagination.vue';
import { getErrorMessage } from '@/utils';
import formatDateMixin from '@/utils/mixins/formatDateMixin';
import BackButton from '@/components/BackButton.vue';

export default {
  name: 'AdminConversationDetail',

  components: {
    BackButton,
    Wrapper,
    DataTable,
    Pagination,
  },

  mixins: [formatDateMixin],

  props: {
    id: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,
      messages: [],
      isEditDialogOpen: false,
      isDeleteDialogOpen: false,
      selectedMsg: null,
      transporter: {},
      shipper: {},
      search: '',
      sort: 'DESC',
      currentPage: 1,
      totalPages: 1,
      options: [
        { label: 'Newest', value: 'DESC' },
        { label: 'Oldest', value: 'ASC' },
      ],
      columns: ['Sender', 'Content', '', 'Time Sent', 'Action'],
    };
  },

  computed: {
    bookingID() {
      const status = get(this.messages[0], 'bookingConversation.quoteResponse.status', null);
      const id = status ? get(this.messages[0], 'bookingConversation.quoteResponse.id', null) : null;
      return id;
    },
    rfqID() {
      return get(this.messages[0], 'bookingConversation.quoteResponse.request.id');
    },
  },

  watch: {
    currentPage() {
      this.debouncedGetRequest();
      window.scrollTo({ top: this.$refs.table.$el.offsetTop, behavior: 'smooth' });
    },
  },

  created() {
    this.getRequest();
    this.debouncedGetRequest = _debounce(this.getRequest, 500);
  },

  methods: {
    getParams() {
      const params = {};

      if (this.search) {
        params.search = this.search;
      }

      if (this.sort !== 'DESC') {
        params.sort = this.sort;
      }

      if (this.currentPage > 1) {
        params.page = this.currentPage;
      }

      return params;
    },
    async getRequest() {
      this.isLoading = true;

      try {
        const params = this.getParams();
        const {
          data: {
            data,
            pagination: { page, pages },
          },
        } = await this.$axios.request(API.admin.getOneConversation({ id: this.id, params }));
        this.messages = data;
        this.currentPage = page;
        this.totalPages = pages;
        if (data.length) {
          this.shipper = data[0].bookingConversation.quoteResponse.request.shipper;
          this.transporter = data[0].bookingConversation.quoteResponse.transporter;
        }
      } catch (err) {
        this.$q.notify({
          message: getErrorMessage(err),
          type: 'negative',
          actions: [{ label: 'Try again', color: 'yellow', handler: this.getRequest }],
        });
      } finally {
        this.isLoading = false;
      }
    },
    debouncedGetRequest() {},
    async editMessage() {
      const data = { content: this.selectedMsg.content };
      try {
        this.isLoading = true;
        await this.$axios.request(API.admin.changeMessage({ id: this.selectedMsg.id, data }));
        this.isEditDialogOpen = false;
        this.selectedMsg = null;
        this.$q.notify({ message: 'Message edited successfully!', type: 'positive' });
        this.getRequest();
      } catch (error) {
        this.$q.notify({ message: getErrorMessage(error), type: 'negative' });
      } finally {
        this.isLoading = false;
      }
    },
    openDialog(type, message) {
      switch (type) {
        case 'delete':
          this.isDeleteDialogOpen = true;
          break;
        case 'edit':
          this.isEditDialogOpen = true;
          break;
        default:
          throw new Error('Unknown type. Must be either "delete" and "edit"');
      }
      this.selectedMsg = { ...message };
    },
    async deleteMessage() {
      try {
        this.isLoading = true;
        await this.$axios.request(API.admin.deleteMessage({ id: this.selectedMsg.id }));
        this.isDeleteDialogOpen = false;
        this.selectedMsg = null;
        this.$q.notify({ message: 'Message successfully deleted!', type: 'positive' });
        await this.getRequest();
      } catch (error) {
        this.$q.notify({ message: getErrorMessage(error), type: 'negative' });
      } finally {
        this.isLoading = false;
      }
    },
    reset() {
      if (!this.search && this.sort === 'DESC') return;

      this.search = '';
      this.sort = 'DESC';
      this.debouncedGetRequest();
    },
  },
};
</script>
